import useSWR, { mutate } from 'swr';
import { useCallback, useMemo } from 'react';

import axiosServices, { fetcher } from 'utils/axios';
import useTenantScopedKey from 'hooks/useTenantScopedKey';
import { currentTenantScopedKey } from 'utils/tenantCacheKey';

export const DEVICE_TYPES = [
  { id: 1, name: 'POS' },
  { id: 2, name: 'Kiosk' },
  { id: 3, name: 'Tablet' }
] as const;

export function getDeviceTypeName(deviceTypeId: number): string {
  return DEVICE_TYPES.find((t) => t.id === deviceTypeId)?.name ?? `Type ${deviceTypeId}`;
}

export interface DeviceResponse {
  deviceId: string;
  deviceName: string;
  hostname: string | null;
  deviceOrdinal: number;
  deviceTypeId: number;
  tenantId: number;
  siteId: number;
  location: string | null;
  settings: string | null;
  appVersion: string | null;
  publicKey: string | null;
  keyAlgorithm: string | null;
  keyVersion: number | null;
  thumbprint: string | null;
  rowVersionHex: string;
  revokedDate: string | null;
  isActive: boolean;
  isApproved: boolean;
  // DeviceStatus join fields
  lastCommunication: string | null;
  lastError: string | null;
  isLeader: boolean | null;
  leaderEpoch: string | null;
}

export interface UpdateDeviceRequest {
  deviceName: string;
  deviceOrdinal: number;
  deviceTypeId: number;
  siteId: number;
  location: string | null;
  settings: string | null;
  appVersion: string | null;
  publicKey: string | null;
  keyAlgorithm: string | null;
  keyVersion: number | null;
  revokedDate: string | null;
  thumbprint: string | null;
  isActive: boolean;
  isApproved: boolean;
}

export interface POSANPRSettingsJson {
  maxUnprocessedEventsToShow: number;
  showMostRecentEventsFirst: boolean;
}

// Mirrors Weightrax.Domain.Models.DeviceTemperatureSensorConfig.
export interface DeviceTemperatureSensorConfigJson {
  label: string | null;
  hardwareName: string;
  temperatureSensorName: string;
  /** Serial port for a directly-attached sensor, e.g. "COM5". Null for discovered hardware. */
  serialPortName: string | null;
  humiditySensorName: string | null;
  lowAlarmSensorName: string | null;
  highAlarmSensorName: string | null;
  criticalAlarmSensorName: string | null;
  lowAlarmValue: number | null;
  highAlarmValue: number | null;
  criticalAlarmValue: number | null;
  lowHumidityAlarmValue: number | null;
  highHumidityAlarmValue: number | null;
}

// Mirrors Weightrax.Domain.Models.DeviceTemperatureSettings.
export interface DeviceTemperatureSettingsJson {
  enabled: boolean;
  pollIntervalSeconds: number;
  outputCsvBasePath: string;
  sensors: DeviceTemperatureSensorConfigJson[];
  retentionDays: number;
  maxUnsentReadings: number;
}

export interface DeviceSettingsJson {
  defaultAppMode: number;
  lastAppMode: number | null;
  rememberLastAppModeOnStartup: boolean;
  autoLogin: boolean;
  autoLoginUser: number | null;
  posANPRSettings: POSANPRSettingsJson;
  docketPrefix: string;
  temperatureLogger: DeviceTemperatureSettingsJson | null;
  canResizeAppWindow: boolean;
  allowCloseApplication: boolean;
  stopWeightraxServiceWhenApplicationCloses: boolean;
  /** Minutes of no activity in each app mode before the device logs the user out. Null never logs out. */
  posLogoutAfterMinutes: number | null;
  kioskLogoutAfterMinutes: number | null;
  tabletLogoutAfterMinutes: number | null;
  /**
   * The traffic this device handles: In = 1, Out = 2, Both = 3. A visit saved on the device drives the barrier
   * arms and traffic lights linked to it, and those used on every device whose direction matches.
   */
  trafficDirection: number;

  /**
   * Settings present in the stored JSON that this client does not model, kept verbatim so they
   * survive a save.
   *
   * The server's DeviceSettings carries more than the admin UI edits — peripheral positions, the
   * next docket number, the on-screen keyboard flag — and Device.Settings is replaced wholesale
   * on update. Without this, saving a device here would quietly discard whatever this file has
   * not been taught about.
   */
  unknownFields: Record<string, unknown>;
}

const DEFAULT_POS_ANPR_SETTINGS: POSANPRSettingsJson = {
  maxUnprocessedEventsToShow: 5,
  showMostRecentEventsFirst: false
};

const DEFAULT_DEVICE_SETTINGS: DeviceSettingsJson = {
  defaultAppMode: 1,
  lastAppMode: null,
  rememberLastAppModeOnStartup: false,
  autoLogin: false,
  autoLoginUser: null,
  posANPRSettings: { ...DEFAULT_POS_ANPR_SETTINGS },
  docketPrefix: '',
  temperatureLogger: null,
  canResizeAppWindow: false,
  allowCloseApplication: true,
  stopWeightraxServiceWhenApplicationCloses: true,
  posLogoutAfterMinutes: null,
  kioskLogoutAfterMinutes: null,
  tabletLogoutAfterMinutes: null,
  trafficDirection: 3,
  unknownFields: {}
};

export const DEFAULT_DEVICE_TEMPERATURE_SETTINGS: DeviceTemperatureSettingsJson = {
  enabled: false,
  pollIntervalSeconds: 60,
  outputCsvBasePath: 'TemperatureReadings',
  sensors: [],
  retentionDays: 30,
  maxUnsentReadings: 250000
};

export function createTemperatureSensorConfig(): DeviceTemperatureSensorConfigJson {
  return {
    label: '',
    hardwareName: '',
    temperatureSensorName: '',
    serialPortName: null,
    humiditySensorName: null,
    lowAlarmSensorName: null,
    highAlarmSensorName: null,
    criticalAlarmSensorName: null,
    lowAlarmValue: null,
    highAlarmValue: null,
    criticalAlarmValue: null,
    lowHumidityAlarmValue: null,
    highHumidityAlarmValue: null
  };
}

/**
 * Every key this file reads, in both the camelCase the cloud writes and the PascalCase the
 * device models use. Anything outside this set is preserved untouched by parse/serialize.
 */
const KNOWN_DEVICE_SETTING_KEYS = new Set(
  [
    'defaultAppMode',
    'lastAppMode',
    'rememberLastAppModeOnStartup',
    'autoLogin',
    'autoLoginUser',
    'posANPRSettings',
    'docketPrefix',
    'temperatureLogger',
    'canResizeAppWindow',
    'allowCloseApplication',
    'stopWeightraxServiceWhenApplicationCloses',
    'posLogoutAfterMinutes',
    'kioskLogoutAfterMinutes',
    'tabletLogoutAfterMinutes',
    'trafficDirection',
    // Retired in favour of the per-mode settings above; listed so a save drops it rather than
    // carrying it forward as an unknown field.
    'logoutAfterMinutes'
  ].flatMap((key) => [key, key.charAt(0).toUpperCase() + key.slice(1), 'POSANPRSettings'])
);

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Zero or less has no sensible meaning for an inactivity timeout, so it is treated as never.
function toPositiveMinutes(value: unknown): number | null {
  const minutes = toNullableNumber(value);
  return minutes != null && minutes > 0 ? Math.floor(minutes) : null;
}

// DirectionEnum: In = 1, Out = 2, Both = 3. Anything else - including a device that predates the setting - is Both.
function toTrafficDirection(value: unknown): number {
  const direction = toNullableNumber(value);
  return direction === 1 || direction === 2 || direction === 3 ? direction : 3;
}

function toNullableString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseTemperatureSensor(raw: unknown): DeviceTemperatureSensorConfigJson {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    label: toNullableString(r.label ?? r.Label),
    hardwareName: String(r.hardwareName ?? r.HardwareName ?? ''),
    temperatureSensorName: String(r.temperatureSensorName ?? r.TemperatureSensorName ?? ''),
    serialPortName: toNullableString(r.serialPortName ?? r.SerialPortName),
    humiditySensorName: toNullableString(r.humiditySensorName ?? r.HumiditySensorName),
    lowAlarmSensorName: toNullableString(r.lowAlarmSensorName ?? r.LowAlarmSensorName),
    highAlarmSensorName: toNullableString(r.highAlarmSensorName ?? r.HighAlarmSensorName),
    criticalAlarmSensorName: toNullableString(r.criticalAlarmSensorName ?? r.CriticalAlarmSensorName),
    lowAlarmValue: toNullableNumber(r.lowAlarmValue ?? r.LowAlarmValue),
    highAlarmValue: toNullableNumber(r.highAlarmValue ?? r.HighAlarmValue),
    criticalAlarmValue: toNullableNumber(r.criticalAlarmValue ?? r.CriticalAlarmValue),
    lowHumidityAlarmValue: toNullableNumber(r.lowHumidityAlarmValue ?? r.LowHumidityAlarmValue),
    highHumidityAlarmValue: toNullableNumber(r.highHumidityAlarmValue ?? r.HighHumidityAlarmValue)
  };
}

function parseTemperatureSettings(raw: unknown): DeviceTemperatureSettingsJson | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const sensors = r.sensors ?? r.Sensors;

  return {
    enabled: Boolean(r.enabled ?? r.Enabled ?? false),
    pollIntervalSeconds: toNullableNumber(r.pollIntervalSeconds ?? r.PollIntervalSeconds) ?? 60,
    outputCsvBasePath: String(r.outputCsvBasePath ?? r.OutputCsvBasePath ?? ''),
    sensors: Array.isArray(sensors) ? sensors.map(parseTemperatureSensor) : [],
    retentionDays: toNullableNumber(r.retentionDays ?? r.RetentionDays) ?? 30,
    maxUnsentReadings: toNullableNumber(r.maxUnsentReadings ?? r.MaxUnsentReadings) ?? 250000
  };
}

function parsePOSANPRSettings(raw: unknown): POSANPRSettingsJson {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_POS_ANPR_SETTINGS };
  const r = raw as Record<string, unknown>;
  return {
    maxUnprocessedEventsToShow: Number(r.maxUnprocessedEventsToShow ?? r.MaxUnprocessedEventsToShow ?? 5) || 0,
    showMostRecentEventsFirst: Boolean(r.showMostRecentEventsFirst ?? r.ShowMostRecentEventsFirst ?? false)
  };
}

export function parseDeviceSettings(settings: string | null): DeviceSettingsJson {
  if (!settings) return { ...DEFAULT_DEVICE_SETTINGS, posANPRSettings: { ...DEFAULT_POS_ANPR_SETTINGS }, unknownFields: {} };
  try {
    const raw = JSON.parse(settings) as Record<string, unknown>;

    const unknownFields = Object.fromEntries(Object.entries(raw).filter(([key]) => !KNOWN_DEVICE_SETTING_KEYS.has(key)));

    return {
      defaultAppMode: Number(raw.defaultAppMode ?? raw.DefaultAppMode ?? 1) || 1,
      lastAppMode: raw.lastAppMode != null ? Number(raw.lastAppMode) : raw.LastAppMode != null ? Number(raw.LastAppMode) : null,
      rememberLastAppModeOnStartup: Boolean(raw.rememberLastAppModeOnStartup ?? raw.RememberLastAppModeOnStartup ?? false),
      autoLogin: Boolean(raw.autoLogin ?? raw.AutoLogin ?? false),
      autoLoginUser: raw.autoLoginUser != null ? Number(raw.autoLoginUser) : raw.AutoLoginUser != null ? Number(raw.AutoLoginUser) : null,
      posANPRSettings: parsePOSANPRSettings(raw.posANPRSettings ?? raw.POSANPRSettings),
      docketPrefix: typeof raw.docketPrefix === 'string' ? raw.docketPrefix : typeof raw.DocketPrefix === 'string' ? raw.DocketPrefix : '',
      temperatureLogger: parseTemperatureSettings(raw.temperatureLogger ?? raw.TemperatureLogger),
      // Defaults to false to match the device model: a device that has never been told otherwise
      // runs its window locked.
      canResizeAppWindow: Boolean(raw.canResizeAppWindow ?? raw.CanResizeAppWindow ?? false),
      // These two default to true to match the device model, so a device whose stored settings
      // predate them keeps the behaviour it has today rather than silently losing the command.
      allowCloseApplication: Boolean(raw.allowCloseApplication ?? raw.AllowCloseApplication ?? true),
      stopWeightraxServiceWhenApplicationCloses: Boolean(
        raw.stopWeightraxServiceWhenApplicationCloses ?? raw.StopWeightraxServiceWhenApplicationCloses ?? true
      ),
      posLogoutAfterMinutes: toPositiveMinutes(raw.posLogoutAfterMinutes ?? raw.PosLogoutAfterMinutes),
      kioskLogoutAfterMinutes: toPositiveMinutes(raw.kioskLogoutAfterMinutes ?? raw.KioskLogoutAfterMinutes),
      tabletLogoutAfterMinutes: toPositiveMinutes(raw.tabletLogoutAfterMinutes ?? raw.TabletLogoutAfterMinutes),
      trafficDirection: toTrafficDirection(raw.trafficDirection ?? raw.TrafficDirection),
      unknownFields
    };
  } catch {
    return { ...DEFAULT_DEVICE_SETTINGS, posANPRSettings: { ...DEFAULT_POS_ANPR_SETTINGS }, unknownFields: {} };
  }
}

export function serializeDeviceSettings(settings: DeviceSettingsJson): string {
  const { unknownFields, temperatureLogger, ...known } = settings;

  // Unmodelled settings go back first so the fields this client owns take precedence, while
  // everything else the device relies on survives the round trip.
  return JSON.stringify({
    ...unknownFields,
    ...known,
    // Omitted entirely rather than written as null, so a device that has never had temperature
    // logging configured keeps a clean settings blob.
    ...(temperatureLogger ? { temperatureLogger } : {})
  });
}

const BASE_ENDPOINT = 'api/devices';

function normalizeList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const list = record.items ?? record.data ?? record.results;
    if (Array.isArray(list)) return list as T[];
  }
  return [];
}

function withCacheBuster(url: string, ts: number): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_ts=${ts}`;
}

export async function updateDevice(deviceId: string, payload: UpdateDeviceRequest): Promise<DeviceResponse> {
  const response = await axiosServices.put<DeviceResponse>(`${BASE_ENDPOINT}/${deviceId}`, payload);
  return response.data;
}

/**
 * Replaces a device's settings document.
 *
 * A separate endpoint from `updateDevice` because the two take different paths server-side.
 * Device administration deliberately preserves the device's own fields — settings included — so
 * sending them through that call silently discards them. Settings are jointly authored by the
 * cloud and the device and settled by logical version, so they go through a synchronized source
 * write and reach the device on its next pull.
 *
 * The device's docket counter is preserved server-side regardless of what is sent here.
 */
export async function updateDeviceSettings(deviceId: string, settings: string): Promise<DeviceResponse> {
  const response = await axiosServices.put<DeviceResponse>(`${BASE_ENDPOINT}/${deviceId}/settings`, { settings });
  return response.data;
}

// Mirrors Weightrax.Contracts.Responses.AllowDeviceReenrolmentResponse.
export interface AllowDeviceReenrolmentResponse {
  deviceId: string;
  deviceName: string;
  reenrolmentAllowedUntil: string;
}

/**
 * Authorises one re-enrolment of this device, for a device whose key is lost or broken.
 *
 * Registration otherwise refuses a device name already in use at the site: the site enrolment code
 * is shared and long-lived, so on its own it must not be enough to replace an existing device's key
 * — doing so would both hand control to the caller and lock the real device out. The window is short
 * and closes as soon as it is used.
 */
export async function allowDeviceReenrolment(deviceId: string): Promise<AllowDeviceReenrolmentResponse> {
  const response = await axiosServices.post<AllowDeviceReenrolmentResponse>(`${BASE_ENDPOINT}/${deviceId}/allow-reenrolment`);
  return response.data;
}

/** A device not heard from within the silence threshold. */
export interface SilentDevice {
  deviceId: string;
  deviceName: string;
  siteId: number;
  siteName: string | null;
  /** Null when the device has never connected. */
  lastCommunication: string | null;
}

/** Communication across devices that can talk to the Cloud: active, approved and not revoked. */
export interface DeviceLastCommunication {
  /** The latest request from any device in scope; null when none has ever connected. */
  lastCommunication: string | null;
  /** The latest sync data a device in scope sent; null when none has. */
  lastDataReceived: string | null;
  /** The least recent last communication among devices that have ever connected. */
  oldestCommunication: string | null;
  /** How long a device may go unheard before it counts as silent; decided by the server. */
  silentAfterMinutes: number;
  silentDeviceCount: number;
  /** The first few silent devices, never-connected first, then the longest silent. May be shorter than the count. */
  silentDevices: SilentDevice[];
}

/**
 * When devices in scope were last heard from, and when they last sent data — the dashboard's device
 * indicator. Omit both ids for the whole tenant.
 */
export async function getLastDeviceCommunication(
  companyId?: number | null,
  siteId?: number | null,
  silentAfterMinutes?: number | null
): Promise<DeviceLastCommunication> {
  const response = await axiosServices.get<Partial<DeviceLastCommunication>>(`${BASE_ENDPOINT}/last-communication`, {
    params: { companyId: companyId ?? undefined, siteId: siteId ?? undefined, silentAfterMinutes: silentAfterMinutes ?? undefined }
  });
  return {
    lastCommunication: response.data.lastCommunication ?? null,
    lastDataReceived: response.data.lastDataReceived ?? null,
    oldestCommunication: response.data.oldestCommunication ?? null,
    silentAfterMinutes: response.data.silentAfterMinutes ?? 0,
    silentDeviceCount: response.data.silentDeviceCount ?? 0,
    silentDevices: response.data.silentDevices ?? []
  };
}

export function useGetDevicesBySite(siteId: number | null) {
  const url = siteId == null ? null : `${BASE_ENDPOINT}/site/${siteId}`;

  const { data, isLoading, error, isValidating } = useSWR<unknown>(useTenantScopedKey(url), () => fetcher(url!), {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const revalidateDevices = useCallback(() => {
    if (url == null) return Promise.resolve(undefined);
    const ts = Date.now();
    return mutate(currentTenantScopedKey(url), fetcher(withCacheBuster(url, ts)), { populateCache: true, revalidate: false });
  }, [url]);

  return useMemo(
    () => ({
      devices: normalizeList<DeviceResponse>(data),
      devicesLoading: siteId == null ? false : isLoading,
      devicesError: error,
      devicesValidating: isValidating,
      revalidateDevices
    }),
    [data, error, isLoading, isValidating, siteId, revalidateDevices]
  );
}
